import { bookingManagementApi } from "./booking-management-api";
import {
    Convert,
    GetHostHubMappingStatusResponse,
    GetHostHubSyncIssuesResponse,
    HostHubUnmappedApartment,
    NeedsRefileItem,
    ReconciliationResponse,
    RentalElement,
    ReservationVersion,
    UnmatchedRentalElement,
} from "@/models/responses";

/**
 * HostHubSyncSummary is the per-batch outcome tally returned by the manual sync
 * trigger (POST /admin/hosthub/sync). It mirrors the Go HostHubSyncResponse
 * struct; there is no generated JSON-schema type for it, so it is declared here.
 */
export interface HostHubSyncSummary {
    created: number;
    modified: number;
    cancelled: number;
    noop: number;
    skipped: number;
}

/**
 * ReconcileArgs is the multipart payload for the read-only Excel reconciliation
 * (POST /admin/reconciliation): the spreadsheet plus the month/year to diff it
 * against current-state HostHub reservations. Nothing is persisted server-side.
 */
export interface ReconcileArgs {
    file: File;
    month: string;
    year: number;
}

/**
 * HostHub reservation-ingestion endpoints (feature hosthub-reservation-ingestion;
 * FR-/ADR- refs below reference its spec.md). INJECTED into the existing
 * bookingManagementApi slice (not a separate createApi) so tag invalidation is
 * shared: a sync or mark-reviewed refreshes the same `Reservations` cache the
 * admin grid reads, plus the `NeedsRefile` queue + its sidebar count.
 */
export const hosthubApi = bookingManagementApi.injectEndpoints({
    endpoints: (builder) => ({
        // Screen 5: manual sync trigger. Invalidates Reservations (grid refetch)
        // and NeedsRefile (a sync may flag filed reservations as needing re-file).
        triggerHostHubSync: builder.mutation<HostHubSyncSummary, void>({
            query: () => ({
                url: "admin/hosthub/sync",
                method: "POST",
            }),
            // HostHubSyncIssues too (hosthub-event-durability/AS-015): a manual
            // run drains wedged rentals, so the issues page and the sidebar
            // badge that read the same cache must refresh together.
            invalidatesTags: ["Reservations", "NeedsRefile", "HostHubSyncIssues"],
        }),

        // Screen 2: version-history timeline for one logical reservation.
        getReservationHistory: builder.query<{ history: ReservationVersion[] }, number>({
            query: (reservationId) => ({
                url: `admin/reservations/${reservationId}/history`,
            }),
            transformResponse: (response: string) => {
                return Convert.toGetReservationHistoryResponse(JSON.stringify(response));
            },
            providesTags: ["Reservations"],
        }),

        // Screen 3: the re-file review queue (flagged reservations + affected filings).
        getNeedsRefile: builder.query<NeedsRefileItem[], void>({
            query: () => ({
                url: "admin/reservations/needs-refile",
            }),
            transformResponse: (response: string) => {
                return Convert.toGetNeedsRefileResponse(JSON.stringify(response)).reservations;
            },
            providesTags: ["NeedsRefile"],
        }),

        // Screen 3: "mark reviewed" action — clears the needs_refile flag. It
        // re-submits nothing; it just refreshes the queue (and its sidebar count)
        // and the reservations grid that surfaces the re-file badge.
        markRefileReviewed: builder.mutation<void, number>({
            query: (reservationId) => ({
                url: `admin/reservations/${reservationId}/refile-reviewed`,
                method: "POST",
            }),
            invalidatesTags: ["NeedsRefile", "Reservations"],
        }),

        // Manual mapping (ADR-013, FR-021): the {id,name} HostHub rentals that
        // populate the apartment-edit rental selector. With no API key the server
        // returns an empty list (never errors), so the edit form still renders.
        getHostHubRentals: builder.query<RentalElement[], void>({
            query: () => ({
                url: "admin/hosthub/rentals",
            }),
            transformResponse: (response: string) => {
                return Convert.toGetHostHubRentalsResponse(JSON.stringify(response)).rentals ?? [];
            },
            // Mapping changes via the apartment update invalidate Apartments;
            // re-using that tag keeps the selector / status view fresh.
            providesTags: ["Apartments"],
        }),

        // Manual mapping: the operator's outstanding-work view — active apartments
        // with no rental mapping, and rentals not referenced by any apartment.
        getHostHubMappingStatus: builder.query<GetHostHubMappingStatusResponse, void>({
            query: () => ({
                url: "admin/hosthub/mapping-status",
            }),
            transformResponse: (response: string): GetHostHubMappingStatusResponse => {
                const parsed = Convert.toGetHostHubMappingStatusResponse(JSON.stringify(response));
                return {
                    unmappedApartments: (parsed.unmappedApartments ?? []) as HostHubUnmappedApartment[],
                    unmatchedRentals: (parsed.unmatchedRentals ?? []) as UnmatchedRentalElement[],
                };
            },
            providesTags: ["Apartments"],
        }),

        // hosthub-event-durability/AS-012: the wedged-rental list plus the
        // freshness of the run it reflects. `lastRunAt` is left undefined when
        // the server omits it (no sync since startup) — the page distinguishes
        // "nothing wedged" from "nothing known yet" via hasRun.
        getHostHubSyncIssues: builder.query<GetHostHubSyncIssuesResponse, void>({
            query: () => ({
                url: "admin/hosthub/sync-issues",
            }),
            transformResponse: (response: string) => {
                return Convert.toGetHostHubSyncIssuesResponse(JSON.stringify(response));
            },
            providesTags: ["HostHubSyncIssues"],
        }),

        // Screen 4: read-only Excel-vs-HostHub reconciliation for a month.
        reconcile: builder.mutation<ReconciliationResponse, ReconcileArgs>({
            query: ({ file, month, year }) => {
                const body = new FormData();
                body.append("file", file);
                body.append("month", month);
                body.append("year", String(year));
                return {
                    url: "admin/reconciliation",
                    method: "POST",
                    body,
                };
            },
            transformResponse: (response: string) => {
                return Convert.toReconciliationResponse(JSON.stringify(response));
            },
        }),
    }),
});

export const {
    useTriggerHostHubSyncMutation,
    useGetReservationHistoryQuery,
    useGetNeedsRefileQuery,
    useMarkRefileReviewedMutation,
    useReconcileMutation,
    useGetHostHubRentalsQuery,
    useGetHostHubMappingStatusQuery,
    useGetHostHubSyncIssuesQuery,
} = hosthubApi;
